// @ts-nocheck: converted from JS

import {
  _isEmpty,
  _isEmptyString,
  _isNil,
  _isNotBlank,
  _isNotEmpty,
  _notNil,
  safelyDecodeURIComponent,
} from '@/littledash';
import { isSubsetOfSets } from '@/utils/browser.utils';
import './MultiSelectEditor.scss';
import styles from './MultiSelectCell.module.scss';
import InVivoError from '@/model/InVivoError.ts';
import { notAborted, useAbortController } from '@/support/Hooks/fetch/useAbortController';
import Http from '@/support/http';
import { api } from '@/support/route';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import Handsontable from 'handsontable';
import { StrictMode, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import SyncSelect from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';
import { type BaseRenderer } from 'handsontable/renderers/base';
import { type CellValue, type RangeType } from 'handsontable/common';
import { fromInVivoValidator, type InVivoValidator } from '@/support/hotValidator.util';
import { validationCache } from '@/utils/ValidationRequestCache';

const loadMultiSelectRawData = (value: string, jsonEncoded: boolean, decode: boolean = false): Array<string> => {
  if (jsonEncoded) {
    try {
      const jsonData = JSON.parse(_isNotBlank(value) ? value : '[]');
      if (Array.isArray(jsonData)) {
        return jsonData.reduce<Array<string>>((acc, item) => {
          switch (typeof item) {
            case 'string':
              if (_isNotBlank(item)) {
                acc.push(item.trim());
              }
              break;
            case 'boolean':
            case 'number':
              acc.push(`${item}`);
              break;
          }
          return acc;
        }, []);
      }
      switch (typeof jsonData) {
        case 'string':
          return [jsonData];
        case 'number':
        case 'boolean':
          return [`${jsonData}`];
        default:
          return [];
      }
    } catch (err) {
      return [];
    }
  }
  return (
    value?.split(',')?.reduce((acc, v) => {
      const trimmedValue = (decode ? safelyDecodeURIComponent(v) : v).trim();
      if (_isNotBlank(trimmedValue)) {
        acc.push(trimmedValue);
      }
      return acc;
    }, []) ?? []
  );
};

const MultiSelect = ({ options, defaultValue, optionsLookupId, td, isMultiSelect, onChange }) => {
  const tdRect = td.getBoundingClientRect();
  const { newAbortController } = useAbortController();
  const selectRef = useRef();

  useEffect(() => {
    selectRef?.current?.focus();
  }, [selectRef]);

  const loadOptions = async (query, loadedOptions, { page, perPage }) => {
    try {
      const url = api('metadata.options.show', { id: optionsLookupId });
      const queryParams = new URLSearchParams({ page, perPage });

      const trimmedQuery = (query ?? '').trim();
      if (_isNotEmpty(trimmedQuery)) {
        queryParams.set('query', trimmedQuery);
      }

      const {
        data: {
          data,
          links: { next },
        },
      } = await Http.get(`${url}?${queryParams.toString()}`, {
        signal: newAbortController().signal,
      });

      const hasMore = _notNil(next);
      const options = data.map(({ value }) => ({
        value: isMultiSelect ? encodeURIComponent(value) : value,
        label: value,
      }));
      let nextPage = page;
      let nextPerPage = perPage;
      if (hasMore) {
        const nextUrl = new URL(next);
        nextPage = nextUrl.searchParams.get('page');
        nextPerPage = nextUrl.searchParams.get('perPage');
      }

      return {
        options,
        hasMore,
        additional: { page: nextPage, perPage: nextPerPage },
      };
    } catch (error) {
      if (notAborted(error)) {
        ExceptionHandler.captureException(
          new InVivoError('Could not load metadata options', { cause: error, slug: 'multi-select-options-load' })
        );
      }
    }
    return { options: [], hasMore: false, additional: { page, perPage } };
  };

  return (
    <div
      style={{ width: `${tdRect.width}px`, height: `${tdRect.height}px` }}
      data-ismulti={isMultiSelect}
      data-test-component="HotMultiselect"
      data-test-element="container"
    >
      {_notNil(optionsLookupId) ? (
        <AsyncPaginate
          selectRef={selectRef}
          classNamePrefix="HoT-Multiselect"
          defaultValue={defaultValue}
          additional={{ page: 1, perPage: 10 }}
          loadOptions={loadOptions}
          isMulti={isMultiSelect}
          closeMenuOnSelect={false}
          autoFocus={true}
          defaultOptions
          menuIsOpen={true}
          cacheOptions={false}
          onChange={onChange}
          debounceTimeout={300}
        />
      ) : (
        <SyncSelect
          ref={selectRef}
          classNamePrefix="HoT-Multiselect"
          defaultValue={defaultValue}
          options={options}
          isMulti={isMultiSelect}
          menuIsOpen={true}
          autoFocus={true}
          closeMenuOnSelect={false}
          onChange={(args) => onChange(args)}
        />
      )}
    </div>
  );
};

const valueToChip = (value: string, valid: boolean = true) => {
  const chip = document.createElement('span');
  chip.classList.add(styles.ValueChip);
  if (!valid) {
    chip.classList.add(styles.InvalidChip);
  }
  chip.innerText = safelyDecodeURIComponent(value);
  return chip;
};

/**
 * This is some HandsonTable madness to add single click to the cell dropdown arrow
 *
 * Based on HandsonTable DatePicker see <a href="https://github.com/handsontable/handsontable/blob/master/handsontable/src/renderers/autocompleteRenderer/autocompleteRenderer.js#L42-L58">here</a>
 *
 * @private
 * @param hotInstance
 * @param td
 * @param row
 * @param column
 */
const addDropdownIconMousedownHandler = (hotInstance, td, row, column) => {
  if (_isNil(hotInstance.multiSelectDropdownArrowListener)) {
    const eventManager = new Handsontable.EventManager(hotInstance);
    hotInstance.multiSelectDropdownArrowListener = (event) => {
      if (Handsontable.dom.hasClass(event.target, 'multiSelectDropdownArrow')) {
        // This is an internal HoT thing ... If single click breaks look here first
        hotInstance.view?._wt?.getSetting(
          'onCellDblClick',
          null,
          // TODO new CellCoords(row, column),
          { row, column },
          td
        );
      }
    };
    eventManager.addEventListener(hotInstance.rootElement, 'mousedown', hotInstance.multiSelectDropdownArrowListener);
    hotInstance.addHookOnce('afterDestroy', () => eventManager.destroy());
  }
};
export const multiSelectInVivoValidator: InVivoValidator = async (value, columnSettings) => {
  const required = columnSettings.required === true;
  const isMultiSelect = (columnSettings as { isMultiSelect?: boolean })?.isMultiSelect ?? true;
  const optionsLookupId = columnSettings?.optionsLookupId ?? null;
  const isLookup = _notNil(columnSettings?.optionsLookupId);

  const selectedOptionsSet = new Set<string>(
    isMultiSelect
      ? loadMultiSelectRawData(value, columnSettings?.jsonEncoded ?? false, isLookup)
      : [(value ?? '').trim()].filter(_isNotBlank)
  );

  if (selectedOptionsSet.size === 0) {
    if (required) {
      return { valid: false, message: 'This field is required' };
    }
    return { valid: true };
  }
  if (isLookup) {
    return validationCache.validate(optionsLookupId, selectedOptionsSet);
  } else if (Array.isArray(columnSettings?.options)) {
    const optionsSet = new Set<string>(
      columnSettings.options.reduce((acc, item) => (_isNotBlank(item) ? [...acc, item.trim()] : acc), [])
    );
    if (!isSubsetOfSets(selectedOptionsSet, optionsSet)) {
      return { valid: false, message: 'Selected options are not valid' };
    }
  }

  return { valid: true };
};

export const multiSelectRenderer: BaseRenderer = (hotInstance, td, row, column, prop, value, cellProperties) => {
  Handsontable.dom.empty(td);
  const isValid = hotInstance.getCellMeta(row, column)?.valid ?? true;
  if (!isValid) {
    td.classList.add('htInvalid');
  }
  const isMultiSelect = cellProperties?.isMultiSelect ?? true;
  const isLookup = _notNil(cellProperties?.optionsLookupId);
  const jsonEncoded = cellProperties?.jsonEncoded ?? false;
  const isReadOnly = cellProperties?.readOnly === true;

  if (isReadOnly) {
    td.style.padding = '0';
  }

  const flexContainer = document.createElement('div');
  flexContainer.classList.add(styles.FlexContainer);
  if (isReadOnly) {
    flexContainer.classList.add(styles.readOnly);
  }

  const valuesContainer = document.createElement('div');
  valuesContainer.classList.add(styles.ValuesContainer);

  if (_isNotBlank(value)) {
    const validOptions = new Set<string>(
      Array.isArray(cellProperties?.options)
        ? cellProperties.options.reduce((acc, item) => (_isNotBlank(item) ? [...acc, item.trim()] : acc), [])
        : []
    );
    if (isMultiSelect === true) {
      loadMultiSelectRawData(value, jsonEncoded).forEach((v) => {
        const chipValue = v.trim();
        if (_isNotEmpty(chipValue)) {
          valuesContainer.appendChild(valueToChip(chipValue, isLookup ? isValid : validOptions.has(chipValue)));
        }
      });
    } else {
      const chipValue = value.trim();
      valuesContainer.appendChild(valueToChip(chipValue, isLookup ? isValid : validOptions.has(chipValue)));
    }
  }

  flexContainer.appendChild(valuesContainer);

  if (!isReadOnly) {
    const dropdownIconContainer = document.createElement('div');
    dropdownIconContainer.classList.add(styles.DropdownIcon, 'multiSelectDropdownArrow');
    dropdownIconContainer.innerText = '▼';
    flexContainer.appendChild(dropdownIconContainer);
  }

  td.appendChild(flexContainer);

  addDropdownIconMousedownHandler(hotInstance, td, row, column);
};

export class MultiSelectEditor extends Handsontable.editors.TextEditor {
  static type = 'multi-select';

  static register(type = MultiSelectEditor.type) {
    Handsontable.cellTypes.registerCellType(type, {
      editor: MultiSelectEditor,
      renderer: multiSelectRenderer,
      validator: fromInVivoValidator(multiSelectInVivoValidator),
    });
  }

  static validatorFactory(options, isMultiSelect, min = 0, max = Infinity) {
    const trimmedOptions = options.map((o) => o.trim());
    return (value, callback) => {
      const selectedOptions = isMultiSelect
        ? String(value ?? '')
            .split(',')
            .map((o) => o.trim())
            .filter(_isNotEmpty)
        : [String(value ?? '').trim()].filter(_isNotEmpty);
      if (
        selectedOptions.length >= min &&
        selectedOptions.length <= max &&
        !selectedOptions.some((option) => !trimmedOptions.includes(option))
      ) {
        callback(true);
      } else {
        callback(false);
      }
    };
  }

  static lookupValidatorFactory(isMultiSelect, min = 0, max = Infinity) {
    return (value, callback) => {
      const selectedOptions = isMultiSelect
        ? String(value ?? '')
            .split(',')
            .filter(_isNotEmpty)
        : [String(value ?? '')].filter(_isNotEmpty);
      if (selectedOptions.length >= min && selectedOptions.length <= max) {
        callback(true);
      } else {
        callback(false);
      }
    };
  }

  displayed = false;

  init() {
    super.init();
    this.instance.addHook('afterDestroy', () => this.destroyElements());
  }

  createElements() {
    super.createElements();
    this.TEXTAREA.style.fontSize = '1px';
    this.TEXTAREA.style.opacity = '0';

    this.multiSelectContainer = this.hot.rootDocument.createElement('div');
    this.multiSelectContainer.classList.add('multi-select-container');
    this.multiSelectContainer.setAttribute('data-testid', 'hot-multi-select-container');

    this.multiSelectContainer.style.display = 'none';

    this.multiSelectOverlay = this.hot.rootDocument.createElement('div');
    this.multiSelectOverlay.classList.add('multi-select-overlay');
    this.multiSelectOverlay.setAttribute('data-testid', 'hot-multi-select-overlay');

    this.multiSelectContainer.appendChild(this.multiSelectOverlay);

    this.hot.rootDocument.body.appendChild(this.multiSelectContainer);

    const handleMouseDown = (event: MouseEvent) => {
      if (_notNil(this.multiSelectContainer) && event.target === this.multiSelectContainer) {
        this.finishEditing(false, false, () => {
          const { row, col } = this.cellProperties;
          this.hot.selectCell(row, col);
        });
      } else {
        event.stopImmediatePropagation();
      }
    };

    const handleClick = (_) => {
      const isMultiSelect = this.multiSelectOverlay?.firstChild?.getAttribute('data-ismulti') === 'true';
      if (!isMultiSelect) {
        const { row, col } = this.cellProperties;
        this.hot.selectCell(row, col);
      }
    };

    const handleKeyDown = (e) => {
      const isMultiSelect = this.multiSelectOverlay?.firstChild?.getAttribute('data-ismulti') === 'true';
      const { row, col } = this.cellProperties;
      if (isMultiSelect) {
        if (e.code === 'Escape') {
          this.hot.selectCell(row, col);
        }
      } else {
        switch (e.code) {
          case 'Enter':
          case 'Escape':
            this.hot.selectCell(row, col);
            break;
        }
      }
      e.stopPropagation();
    };

    this.multiSelectContainer.addEventListener('mousedown', handleMouseDown, {
      passive: false,
    });
    this.multiSelectContainer.addEventListener('click', handleClick, {
      passive: true,
    });
    this.multiSelectContainer.addEventListener('keydown', handleKeyDown);

    this.hot.addHookOnce('afterDestroy', () => {
      this.multiSelectContainer.removeEventListener('mousedown', handleMouseDown);
      this.multiSelectContainer.removeEventListener('click', handleClick);
      this.multiSelectContainer.removeEventListener('keydown', handleKeyDown);
    });
  }

  render() {
    if (
      this.isOpened() &&
      _notNil(this.multiSelectOverlay) &&
      _notNil(this.cellProperties) &&
      this.multiSelectContainer?.style?.display === 'block'
    ) {
      const isMultiSelect = this.cellProperties?.isMultiSelect ?? true;
      const optionsLookupId = this.cellProperties?.optionsLookupId ?? null;
      const isLookup = _notNil(optionsLookupId);
      const jsonEncoded = this.cellProperties?.jsonEncoded ?? false;
      const optionsComparator: (<T extends { label: string }>(a: T, b: T) => number) | undefined =
        this.cellProperties?.optionsComparator;
      const optionsMap: Map<string, { value: string; label: string }> =
        this.cellProperties?.optionsMap ??
        new Map(
          (this.cellProperties?.options ?? []).reduce((acc, option: string | { value: string; label: string }) => {
            if (_isNotBlank(option)) {
              const value = option.trim();
              acc.push([value, { value: value, label: isMultiSelect ? safelyDecodeURIComponent(value) : value }]);
            } else if (
              typeof option === 'object' &&
              Object.prototype.hasOwnProperty.call(option, 'value') &&
              _isNotBlank(option.value) &&
              Object.prototype.hasOwnProperty.call(option, 'label') &&
              _isNotEmpty(option.label)
            ) {
              acc.push([option.value, option]);
            }
            return acc;
          }, [])
        );
      const rawValue = this.getValue();
      const defaultValue = (isMultiSelect ? loadMultiSelectRawData(rawValue, jsonEncoded) : [rawValue ?? '']).reduce(
        (acc, optionValue) => {
          if (_isNotBlank(optionValue)) {
            if (isLookup) {
              acc.push({
                label: isMultiSelect ? safelyDecodeURIComponent(optionValue) : optionValue,
                value: optionValue,
              });
            } else {
              const option = optionsMap.get(optionValue.trim());
              if (_notNil(option)) {
                acc.push(option);
              }
            }
          }
          return acc;
        },
        []
      );
      const handleOnChange = (value) => {
        if (isMultiSelect) {
          const updatedData = value.reduce<Array<string>>(
            (acc, item) => (_isNotBlank(item?.value) ? [...acc, item.value] : acc),
            []
          );
          this.setValue(jsonEncoded ? JSON.stringify(updatedData) : updatedData.join(','));
        } else {
          this.setValue(value?.value ?? null);
          this.finishEditing();
        }
      };
      if (_isNil(this.root)) {
        this.root = ReactDOM.createRoot(this.multiSelectOverlay);
      }
      this.root.render(
        <StrictMode>
          <MultiSelect
            options={Array.from(optionsMap.values()).sort(
              optionsComparator ??
                ((left, right) => left.label.localeCompare(right.label, undefined, { numeric: true }))
            )}
            defaultValue={defaultValue}
            optionsLookupId={optionsLookupId}
            td={this.TD}
            isMultiSelect={isMultiSelect}
            onChange={handleOnChange}
          />
        </StrictMode>
      );
    }
  }

  destroyElements() {
    Handsontable.dom.empty(this.multiSelectContainer);
    this.multiSelectContainer.remove();
  }

  finishEditing(restoreOriginalValue = false, ctrlDown = false, callback = null) {
    if (restoreOriginalValue && _notNil(this.originalValue)) {
      this.setValue(this.originalValue);
    }
    super.finishEditing(restoreOriginalValue, ctrlDown, callback);
  }

  setValue(newValue) {
    super.setValue(newValue);
    this.render();
  }

  getValue() {
    return super.getValue() ?? '';
  }

  open(event = null) {
    super.open();
    this.display();
  }

  close() {
    this.display(false);
    super.close();
  }

  isOpened() {
    return this.displayed;
  }

  display(display = true) {
    this.displayed = display;
    if (display) {
      const offset = this.TD.getBoundingClientRect();
      this.multiSelectOverlay.style.top = `${offset.top}px`;
      this.multiSelectOverlay.style.left = `${offset.left}px`;
      this.TEXTAREA.style.width = `${offset.width}px`;
      this.TEXTAREA.style.height = `${offset.height}px`;
      this.multiSelectContainer.style.display = 'block';
      this.render();
    } else {
      this.multiSelectContainer.style.display = 'none';
      window.requestAnimationFrame(() => {
        this.root?.unmount();
        this.root = null;
        Handsontable.dom.empty(this.multiSelectOverlay);
      });
    }
  }

  focus() {}
}

/**
 * Converts a pasted value to JSON array format for jsonEncoded multi-select cells
 * @param value - The pasted value to convert
 * @returns JSON string representing an array
 */
function convertToJsonArray(value: string): string {
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return value;
    }
  } catch {
    // Intentionally empty
  }
  return JSON.stringify([value]);
}

/**
 * AfterPaste handler for jsonEncoded multi-select cells
 * Auto-converts plain strings to JSON array format while preserving all user input
 * Based on timepointAfterPasteHandler pattern
 */
export const multiSelectAfterPasteHandler = function (
  this: Handsontable,
  data: CellValue[][],
  coords: RangeType[]
): void {
  if (_isEmpty(coords) || _isEmpty(data)) {
    return;
  }

  try {
    const cellUpdates: Array<[number, number, string]> = [];

    for (const { startRow, startCol, endRow, endCol } of coords) {
      const actualEndRow = Math.max(endRow, startRow + data.length - 1);
      for (let row = startRow; row <= actualEndRow; row++) {
        for (let col = startCol; col <= endCol; col++) {
          const cellMeta = this.getCellMeta(row, col);

          if (cellMeta.type === MultiSelectEditor.type && cellMeta.jsonEncoded && cellMeta.isMultiSelect) {
            const dataRowIndex = (row - startRow) % data.length;
            const dataColIndex = (col - startCol) % (data[0]?.length ?? 1);
            const pastedValue = data[dataRowIndex]?.[dataColIndex];

            if (_isNil(pastedValue) || _isEmptyString(pastedValue)) {
              continue;
            } else {
              const convertedValue = convertToJsonArray(String(pastedValue));
              cellUpdates.push([row, col, convertedValue]);
            }
          }
        }
      }
    }

    if (_isNotEmpty(cellUpdates)) {
      this.setDataAtCell(cellUpdates, 'edit');
      setTimeout(() => this.validateCells(), 100);
    }
  } catch (error) {
    ExceptionHandler.captureException(
      new InVivoError('Error in multiselect paste handler', {
        slug: 'multiselect-paste-handler-error',
        cause: error,
        level: 'error',
      })
    );
  }
};
